import { CurrencyAmount, Token, V2_ROUTER_ADDRESSES } from '@kodiak-finance/sdk-core'
import CurrencyInputPanel from 'components/CurrencyInputPanel'
import CurrencyLogo from 'components/CurrencyLogo'
import FormattedCurrencyAmount from 'components/FormattedCurrencyAmount'
import Loader from 'components/Loader'
import { RowFixed } from 'components/Row'
import { useApproveStep } from 'components/StepLine/common/ApproveStep'
import Step from 'components/StepLine/common/Step'
import useStepWithNext from 'components/StepLine/context/hooks/useStepWithNext'
import StepLineModal from 'components/StepLine/StepLineModal'
import Toggle from 'components/Toggle'
import { BAULT_ROUTER_ADDRESSES } from 'constants/addresses'
import { useToken } from 'hooks/Tokens'
import { useBaultDatas } from 'hooks/useBault'
import { useConnection } from 'hooks/useConnection'
import { KodiakFarmStake, useKodiakFarmLockedStakesOf } from 'hooks/useKodiakFarm'
import { useNativeCurrencyPreference } from 'hooks/useNativeCurrencyPreference'
import usePercentageHandlers from 'hooks/usePercentageHandlers'
import usePoolPositionDetails, { usePoolUnderlyingBalances } from 'hooks/usePoolPositionDetails'
import { useSmartIslandRouter } from 'hooks/useSmartIslandRouter'
import { useGetTokenPrice } from 'hooks/useTokenPrice'
import { useWriteContract } from 'hooks/useTransaction'
import useTransactionDeadline from 'hooks/useTransactionDeadline'
import { useUSDValue } from 'hooks/useUSDValue'
import sumCurrencyAmount from 'lib/utils/sumCurrencyAmount'
import tryParseCurrencyAmount from 'lib/utils/tryParseCurrencyAmount'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Text } from 'rebass'
import { Field } from 'state/burn/actions'
import { useBurnActionHandlers, useDerivedBurnInfo } from 'state/burn/hooks'
import { useBurnCharmActionHandlers, useDerivedCharmBurnInfo } from 'state/charm/burn/hooks'
import { useDerivedIslandBurnInfo } from 'state/islands/burn/hooks'

import { ButtonError, ButtonLight } from '../../../../components/Button'
import { LightCard } from '../../../../components/Card'
import { AutoColumn } from '../../../../components/Column'
import DoubleCurrencyLogo from '../../../../components/DoubleLogo'
import { RowBetween } from '../../../../components/Row'
import { useToggleWalletModal } from '../../../../state/application/hooks'
import { useTransactionAdder } from '../../../../state/transactions/hooks'
import { TransactionType } from '../../../../state/transactions/types'
import { ThemedText } from '../../../../theme'
import { IslandContextType } from '../context/IslandContextType'
import { useOnBaultRedeemCallback, useOnUnstakeCallback, useOnVaultUnstakeCallback } from './hooks/useOnUnstakeCallback'
import { useStakingPosition } from './hooks/useStakingPositions'
import { useVaultStrategy } from './hooks/useVaultStrategy'
// import BaultWithdrawConstructorStep from './steps/BaultWithdrawConstructorStep'
import RewardVaultWithdrawConstructorStep from './steps/RewardVaultWithdrawConstructorStep'
import WithdrawBalanceConstructorStep from './steps/WithdrawBalanceConstructorStep'
import WithdrawFromIslandStep from './steps/WithdrawFromIslandStep'
import WithdrawFromRewardVaultStep from './steps/WithdrawFromRewardVaultStep'
import WithdrawFromStakeStep from './steps/WithdrawFromStakeStep'
import { WithdrawParams } from './strategies/vault/VaultStrategy.type'

const WithdrawView: React.FC<IslandContextType> = (context) => {
  const {
    currencyA,
    currencyB,
    feeAmount,
    poolType,
    islandId: poolAddress,
    routerAddress,
    poolApiResponse,
    stakingData,
  } = context

  // ==========================
  // HOOKS & STATE
  // ==========================
  const { account, wagmiPublicClient, chainId } = useConnection()
  const { writeContractAsync } = useWriteContract()
  const [withdrawValue, setWithdrawValue] = useState('')
  const isCharmPool = poolType === 'Charm'
  const [receiveWETH, setReceiveWETH] = useState(isCharmPool ? true : false)
  const toggleWalletModal = useToggleWalletModal()
  const addTransaction = useTransactionAdder()

  const currencyPriceData = useGetTokenPrice(poolAddress)
  const currencyUSDValue = useUSDValue(withdrawValue, currencyPriceData.price)
  const transactionDeadline = useTransactionDeadline()

  const { nativeCurrency, getCurrencyWithPreference, shouldShowNativeCurrencyToggle } =
    useNativeCurrencyPreference(receiveWETH)
  const nativeWrappedSymbol = nativeCurrency.wrapped?.symbol ?? ''

  // For Charm pools keep wrapped tokens as-is (no native display).
  const currencyAForDisplay = useMemo(
    () => (isCharmPool && currencyA?.isNative ? currencyA.wrapped : currencyA),
    [currencyA, isCharmPool]
  )
  const currencyBForDisplay = useMemo(
    () => (isCharmPool && currencyB?.isNative ? currencyB.wrapped : currencyB),
    [currencyB, isCharmPool]
  )

  const currencyAWithPreference = useMemo(
    () => getCurrencyWithPreference(currencyAForDisplay),
    [currencyAForDisplay, getCurrencyWithPreference]
  )
  const currencyBWithPreference = useMemo(
    () => getCurrencyWithPreference(currencyBForDisplay),
    [currencyBForDisplay, getCurrencyWithPreference]
  )

  const showCollectAsWeth = useMemo(
    () => !isCharmPool && shouldShowNativeCurrencyToggle(currencyAForDisplay, currencyBForDisplay),
    [currencyAForDisplay, currencyBForDisplay, isCharmPool, shouldShowNativeCurrencyToggle]
  )

  // ==========================
  // VAULT STRATEGY FOR POOL WITHDRAWALS
  // ==========================
  const vaultType = useMemo(() => {
    switch (poolType) {
      case 'Island':
        return 'island'
      case 'Charm':
        return 'charmvault'
      case 'V2':
        return 'v2'
      default:
        return 'island'
    }
  }, [poolType])

  const smartIslandRouter = useSmartIslandRouter({
    mode: 'doubleSided',
    tokens: [],
    needsBaultFeatures: false,
  })

  // Setup vault strategy with dependencies
  const dependencies = useMemo(() => {
    switch (vaultType) {
      case 'island':
        return {
          smartIslandRouterAddress: smartIslandRouter?.address,
          wagmiPublicClient,
          writeContractAsync,
          chainId,
        }
      case 'charmvault':
        return {
          charmVaultAddress: poolAddress,
          wagmiPublicClient,
          writeContractAsync,
          chainId,
        }
      case 'v2':
        return {
          v2RouterAddress: V2_ROUTER_ADDRESSES[chainId],
          wagmiPublicClient,
          writeContractAsync,
          chainId,
        }
      default:
        return null
    }
  }, [poolAddress, vaultType, smartIslandRouter?.address, wagmiPublicClient, writeContractAsync, chainId])

  const { strategy: poolWithdrawStrategy } = useVaultStrategy(vaultType, dependencies)

  // ==========================
  // POSITION & BALANCE DATA
  // ==========================
  const { position, kodiakFarm } = useStakingPosition({
    stakingData,
  })

  const poolCurrency = useToken(poolAddress)
  const positionDetails = usePoolPositionDetails(poolApiResponse ?? null, currencyA, currencyB, account)
  const { data: baultData } = useBaultDatas(stakingData.baultAddress, account)
  const { data: kodiakFarmStakesData } = useKodiakFarmLockedStakesOf(
    stakingData.stakingType === 'kodiakFarm' ? kodiakFarm : null,
    account
  )

  // Calculate total user balance including all staked positions
  const totalUserBalanceShares = useMemo(() => {
    const baseBalance = positionDetails?.userBalanceShares

    if (position.type === 'kodiakFarm' && position.userStaked) {
      return baseBalance ? baseBalance.add(position.userStaked) : position.userStaked
    } else if (position.type === 'rewardVault') {
      if (position.subType === 'direct' && position.userStaked) {
        return baseBalance ? baseBalance.add(position.userStaked) : position.userStaked
      } else if (position.subType === 'bault' && baultData?.estimatedLpTokens) {
        return baseBalance
      }
    }

    return baseBalance
  }, [position, positionDetails?.userBalanceShares, baultData])

  // ==========================
  // BURN INFO
  // ==========================
  const { onUserInput: v2OnUserInput } = useBurnActionHandlers()
  const { onPercentSelect: onCharmPercentSelect } = useBurnCharmActionHandlers()

  const v2BurnInfo = useDerivedBurnInfo(currencyA, currencyB)
  const islandBurnInfo = useDerivedIslandBurnInfo({
    position: {
      error: positionDetails?.error ?? null,
      poolCurrency: positionDetails?.poolCurrency,
      totalSupply: positionDetails?.totalSupply,
      poolUnderlyingBalances: positionDetails?.poolUnderlyingBalances,
      userBalanceShares: positionDetails?.userBalanceShares,
      userUnderlyingBalances: positionDetails?.userUnderlyingBalances,
      userLiquidityPercentage: positionDetails?.userLiquidityPercentage,
      pool: poolApiResponse ?? null,
      userBalanceSharesUSD: positionDetails?.userBalanceSharesUSD,
    },
    asWETH: receiveWETH,
    feeAmount,
  })

  const charmBurnInfo = useDerivedCharmBurnInfo(poolType === 'Charm' ? poolAddress : undefined)

  const { discountedAmountShares, liquidityValue0, liquidityValue1, amount0Min, amount1Min, error } = useMemo(() => {
    if (poolType === 'V2') {
      const { pair, parsedAmounts } = v2BurnInfo
      return {
        discountedAmountShares: parsedAmounts[Field.LIQUIDITY]?.quotient,
        liquidityValue0: parsedAmounts[Field.CURRENCY_A],
        liquidityValue1: parsedAmounts[Field.CURRENCY_B],
        // Calculate minimums with 1% slippage
        amount0Min: parsedAmounts[Field.CURRENCY_A]?.multiply(99).divide(100),
        amount1Min: parsedAmounts[Field.CURRENCY_B]?.multiply(99).divide(100),
        error: v2BurnInfo.error,
      }
    } else if (poolType === 'Charm') {
      const {
        liquidityValue0,
        liquidityValue1,
        amount0Min,
        amount1Min,
        sharesToBurn,
        userVaultBalance,
        vaultTotalSupply,
      } = charmBurnInfo
    }
    return islandBurnInfo
  }, [poolType, v2BurnInfo, islandBurnInfo])

  // Update burn states
  useEffect(() => {
    if (poolType === 'V2' && withdrawValue) {
      v2OnUserInput(Field.LIQUIDITY, withdrawValue || '0')
    }
  }, [withdrawValue, v2OnUserInput, poolType])

  // ==========================
  // WITHDRAWAL AMOUNTS
  // ==========================
  const selectedShares = useMemo(() => {
    const currency = totalUserBalanceShares?.currency || poolCurrency
    if (currency && withdrawValue && withdrawValue !== '0') {
      const parsed = tryParseCurrencyAmount(withdrawValue, currency)
      return parsed || CurrencyAmount.fromRawAmount(currency, '0')
    }
    return currency ? CurrencyAmount.fromRawAmount(currency, '0') : undefined
  }, [withdrawValue, totalUserBalanceShares, poolCurrency])
  const selectedUnderlyingBalances = usePoolUnderlyingBalances(
    poolApiResponse ?? null,
    selectedShares as CurrencyAmount<Token>,
    currencyAWithPreference,
    currencyBWithPreference
  )

  useEffect(() => {
    if (poolType === 'Charm' && selectedShares && totalUserBalanceShares && !totalUserBalanceShares.equalTo('0')) {
      const percent = parseFloat(selectedShares.divide(totalUserBalanceShares).multiply('100').toFixed(0))
      onCharmPercentSelect(percent)
    }
  }, [poolType, selectedShares, totalUserBalanceShares, onCharmPercentSelect])

  // ==========================
  // PERCENTAGE HANDLERS
  // ==========================
  const {
    handleMax: handleMaxWithdraw,
    handle10Percent: handle10PercentWithdraw,
    handle25Percent: handle25PercentWithdraw,
    handle50Percent: handle50PercentWithdraw,
  } = usePercentageHandlers(totalUserBalanceShares, setWithdrawValue, () => {
    if (totalUserBalanceShares) {
      setWithdrawValue(totalUserBalanceShares.toExact())
    }
  })

  // ==========================
  // MODAL STATE
  // ==========================
  const [showConfirm, setShowConfirm] = useState(false)
  const [attemptingTxn, setAttemptingTxn] = useState(false)
  const [txnHash, setTxnHash] = useState('')

  // Withdrawal amounts for each step
  const [poolLiquidityToWithdraw, setPoolLiquidityToWithdraw] = useState<CurrencyAmount<Token> | undefined>()
  const [stakesToWithdraw, setStakesToWithdraw] = useState<KodiakFarmStake[]>([])
  const [rewardVaultToWithdraw, setRewardVaultToWithdraw] = useState<CurrencyAmount<Token> | undefined>()
  const [baultSharesToWithdraw, setBaultSharesToWithdraw] = useState<CurrencyAmount<Token> | undefined>()

  // ==========================
  // SELECTION HANDLERS
  // ==========================
  const onSelectKodiakFarmShares = useCallback(
    ({
      poolLiquidity,
      stakes: activeStakes,
    }: {
      poolLiquidity: CurrencyAmount<Token> | undefined
      stakes: KodiakFarmStake[]
    }) => {
      setStakesToWithdraw(activeStakes)
      const sum = sumCurrencyAmount(
        activeStakes.map((item) => item.liquidity),
        poolLiquidity
      )
      setPoolLiquidityToWithdraw(sum)
    },
    []
  )

  const onSelectRewardVaultShares = useCallback(
    ({
      poolLiquidity,
      rewardVaultAmount,
    }: {
      poolLiquidity: CurrencyAmount<Token> | undefined
      rewardVaultAmount?: CurrencyAmount<Token>
    }) => {
      setRewardVaultToWithdraw(rewardVaultAmount)
      const sum = sumCurrencyAmount([rewardVaultAmount], poolLiquidity)
      setPoolLiquidityToWithdraw(sum)
    },
    []
  )

  const onSelectBaultShares = useCallback(
    ({
      poolLiquidity,
      baultShares,
      lpTokensToReceive,
    }: {
      poolLiquidity: CurrencyAmount<Token> | undefined
      baultShares?: CurrencyAmount<Token>
      lpTokensToReceive?: CurrencyAmount<Token>
    }) => {
      setBaultSharesToWithdraw(baultShares)
      const sum = sumCurrencyAmount([lpTokensToReceive], poolLiquidity)
      setPoolLiquidityToWithdraw(sum)
    },
    []
  )

  // ==========================
  // WITHDRAWAL HANDLERS
  // ==========================

  const {
    onConfirmWithdrawStakes,
    attemptingTxn: attemptingFarmTxn,
    hash: farmHash,
    willForfeitRewards,
    clear: clearFarm,
  } = useOnUnstakeCallback(stakesToWithdraw, kodiakFarm?.address)

  // Unstake from vault (using existing hook)
  const {
    onConfirmWithdrawStakes: onConfirmVaultWithdraw,
    attemptingTxn: attemptingVaultTxn,
    hash: vaultHash,
    clear: clearVault,
  } = useOnVaultUnstakeCallback(rewardVaultToWithdraw, stakingData.rewardVaultAddress, () => {
    setRewardVaultToWithdraw(undefined)
  })

  const baultRouterAddress = BAULT_ROUTER_ADDRESSES[chainId]
  const {
    onConfirmRedeem: onConfirmBaultWithdraw,
    attemptingTxn: attemptingBaultTxn,
    hash: baultHash,
    clear: clearBault,
  } = useOnBaultRedeemCallback(baultSharesToWithdraw, stakingData.baultAddress, baultRouterAddress, account)

  const poolLiquidityUnderlyingToWithdraw = usePoolUnderlyingBalances(
    poolApiResponse ?? null,
    poolLiquidityToWithdraw,
    currencyAWithPreference,
    currencyBWithPreference
  )

  const onConfirmWithdraw = useCallback(async () => {
    if (!poolWithdrawStrategy || !account || !poolLiquidityToWithdraw || !transactionDeadline) return

    if (!poolLiquidityUnderlyingToWithdraw.amount0 || !poolLiquidityUnderlyingToWithdraw.amount1) {
      console.error('Missing underlying balances for withdrawal')
      return
    }

    try {
      setAttemptingTxn(true)

      const params: WithdrawParams = {
        poolAddress,
        amount: poolLiquidityToWithdraw,
        currencyA: currencyAWithPreference,
        currencyB: currencyBWithPreference,
        amount0Min: poolLiquidityUnderlyingToWithdraw.amount0.multiply(99).divide(100).quotient.toString(),
        amount1Min: poolLiquidityUnderlyingToWithdraw.amount1.multiply(99).divide(100).quotient.toString(),
        account,
        deadline: transactionDeadline,
      }

      const hash = await poolWithdrawStrategy.withdrawLiquidity(params)

      const txType = poolType === 'V2' ? TransactionType.REMOVE_LIQUIDITY_V2 : TransactionType.REMOVE_LIQUIDITY_ISLAND

      addTransaction(
        { hash },
        {
          type: txType,
          islandId: poolAddress,
          tokenA: currencyAWithPreference.isNative
            ? currencyAWithPreference.wrapped.address
            : currencyAWithPreference.address,
          tokenB: currencyBWithPreference.isNative
            ? currencyBWithPreference.wrapped.address
            : currencyBWithPreference.address,
          sharesBurned: poolLiquidityToWithdraw.toExact(),
          tokenAAmount: poolLiquidityUnderlyingToWithdraw.amount0.quotient.toString(),
          tokenBAmount: poolLiquidityUnderlyingToWithdraw.amount1.quotient.toString(),
        }
      )

      setTxnHash(hash)
    } catch (error) {
      console.error(`Failed to remove liquidity from ${poolType}:`, error)
      throw error
    } finally {
      setAttemptingTxn(false)
    }
  }, [
    poolWithdrawStrategy,
    account,
    poolLiquidityToWithdraw,
    poolLiquidityUnderlyingToWithdraw.amount0,
    poolLiquidityUnderlyingToWithdraw.amount1,
    poolAddress,
    currencyAWithPreference,
    currencyBWithPreference,
    poolType,
    addTransaction,
    transactionDeadline?.toString(),
  ])

  // ==========================
  // MODAL HANDLERS
  // ==========================
  const handleDismissConfirmation = useCallback(() => {
    setShowConfirm(false)
    if (txnHash || farmHash) {
      setWithdrawValue('')
    }
    setAttemptingTxn(false)
    setTxnHash('')
    clearVault()
    clearFarm()
  }, [txnHash, farmHash, clearVault, clearFarm])

  // ==========================
  // APPROVAL & STEPS
  // ==========================
  const routerAddressForApproval = useMemo(() => {
    if (poolType === 'V2') {
      return routerAddress
    } else {
      return smartIslandRouter?.address || routerAddress
    }
  }, [poolType, routerAddress, smartIslandRouter])

  const approveStep = useApproveStep(selectedShares, routerAddressForApproval as string)

  // Constructor steps
  const farmWithdrawConstructorStep = useStepWithNext(
    'WithdrawBalanceConstructorStep',
    WithdrawBalanceConstructorStep,
    {
      selectedShares,
      userBalanceShares: positionDetails?.userBalanceShares,
      stakes: kodiakFarmStakesData?.avaliableStakes,
      pool: poolApiResponse,
      onSelect: onSelectKodiakFarmShares,
      collectAsWrapped: receiveWETH,
      overrideCurrencyA: currencyAWithPreference,
      overrideCurrencyB: currencyBWithPreference,
    }
  )

  const rewardVaultWithdrawConstructorStep = useStepWithNext(
    'RewardVaultWithdrawConstructorStep',
    RewardVaultWithdrawConstructorStep,
    {
      selectedShares,
      userBalanceShares: positionDetails?.userBalanceShares,
      rewardVaultStaked:
        position.type === 'rewardVault' && position.subType === 'direct' ? position.userStaked : undefined,
      pool: poolApiResponse,
      percentSelected:
        selectedShares && totalUserBalanceShares && !totalUserBalanceShares.equalTo('0')
          ? parseFloat(selectedShares.divide(totalUserBalanceShares).multiply('100').toFixed(0))
          : 0,
      onSelect: onSelectRewardVaultShares,
      collectAsWrapped: receiveWETH,
      overrideCurrencyA: currencyAWithPreference,
      overrideCurrencyB: currencyBWithPreference,
    }
  )

  const withdrawConstructorStep = useMemo(() => {
    if (position.type === 'kodiakFarm') {
      return farmWithdrawConstructorStep
    } else if (position.type === 'rewardVault') {
      return rewardVaultWithdrawConstructorStep
    }
    return farmWithdrawConstructorStep
  }, [position, farmWithdrawConstructorStep, rewardVaultWithdrawConstructorStep])

  // Withdrawal steps
  const withdrawFromStakeStep = useStepWithNext('WithdrawFromStakeStep', WithdrawFromStakeStep, {
    stakes: stakesToWithdraw,
    attemptingTxn: attemptingFarmTxn,
    kodiakFarm,
    willForfeitRewards,
    pendingText: <>Withdraw from staking {stakesToWithdraw.length} positions</>,
    hash: farmHash,
    onWithdraw: onConfirmWithdrawStakes,
    autoConfirm: true,
    transactionWaiting: true,
  })

  const withdrawFromRewardVaultStep = useStepWithNext('WithdrawFromRewardVaultStep', WithdrawFromRewardVaultStep, {
    amount: rewardVaultToWithdraw,
    rewardVault: stakingData.rewardVaultAddress,
    onWithdraw: onConfirmVaultWithdraw,
    hash: vaultHash,
    attemptingTxn: attemptingVaultTxn,
    pendingText: (
      <>
        Unstaking {rewardVaultToWithdraw?.toSignificant(6) || '0'} {rewardVaultToWithdraw?.currency.symbol}
      </>
    ),
    autoConfirm: true,
    transactionWaiting: true,
    pool: poolApiResponse ?? null,
  })

  const withdrawFromPoolStep = useStepWithNext('WithdrawFromIslandStep', WithdrawFromIslandStep, {
    shares: poolLiquidityToWithdraw,
    pool: poolApiResponse,
    attemptingTxn,
    collectAsWrapped: receiveWETH,
    overrideCurrencyA: currencyAWithPreference,
    overrideCurrencyB: currencyBWithPreference,
    pendingText: (
      <>
        Withdraw from {poolType === 'Charm' ? 'Charm vault' : poolType === 'V2' ? 'V2 pool' : 'Island'}{' '}
        {poolLiquidityToWithdraw?.toSignificant(6)} {poolLiquidityToWithdraw?.currency.symbol}
      </>
    ),
    type: poolType,
    hash: txnHash,
    onWithdraw: onConfirmWithdraw,
    transactionWaiting: true,
  })

  // Build steps array
  const steps = [
    poolType !== 'Charm' ? approveStep : null,
    withdrawConstructorStep,
    ...(position.type === 'kodiakFarm' && stakesToWithdraw.length > 0 ? [withdrawFromStakeStep] : []),
    ...(position.type === 'rewardVault' &&
    position.subType === 'direct' &&
    rewardVaultToWithdraw &&
    !rewardVaultToWithdraw.equalTo('0')
      ? [withdrawFromRewardVaultStep]
      : []),

    withdrawFromPoolStep,
  ].filter((step): step is Step => step !== null)

  // ==========================
  // RENDER
  // ==========================
  const fallbackCurrencies = useMemo(
    () => ({
      currency0: currencyAWithPreference,
      currency1: currencyBWithPreference,
      amount0: currencyAWithPreference ? CurrencyAmount.fromRawAmount(currencyAWithPreference, '0') : undefined,
      amount1: currencyBWithPreference ? CurrencyAmount.fromRawAmount(currencyBWithPreference, '0') : undefined,
    }),
    [currencyAWithPreference, currencyBWithPreference]
  )

  return (
    <>
      <StepLineModal
        isOpen={showConfirm}
        onDismiss={handleDismissConfirmation}
        steps={steps}
        header={<>Withdraw from {poolType === 'Charm' ? 'Charm Vault' : poolType === 'V2' ? 'V2 Pool' : 'Island'}</>}
        onFinish={handleDismissConfirmation}
      />

      {positionDetails ? (
        <AutoColumn gap="lg">
          <RowBetween>
            <RowFixed>
              <DoubleCurrencyLogo currency0={currencyA} currency1={currencyB} size={20} margin={true} />
              <ThemedText.DeprecatedLabel
                ml="10px"
                fontSize="20px"
              >{`${currencyA?.symbol}/${currencyB?.symbol}`}</ThemedText.DeprecatedLabel>
            </RowFixed>
          </RowBetween>

          <CurrencyInputPanel
            value={withdrawValue}
            onUserInput={setWithdrawValue}
            onMax={() => handleMaxWithdraw()}
            showMaxButton={true}
            currency={poolCurrency}
            id="withdraw-liquidity-input"
            showCommonBases
            renderBalance={() => (
              <>
                {totalUserBalanceShares?.toSignificant(6) || '0'} {poolCurrency?.symbol}
              </>
            )}
            label="Amount to Withdraw"
            showPercentButtons={true}
            percentButtonHandlers={{
              onSelect10Percent: handle10PercentWithdraw,
              onSelect25Percent: handle25PercentWithdraw,
              onSelect50Percent: handle50PercentWithdraw,
              onSelectMax: handleMaxWithdraw,
            }}
            apiUSDValue={currencyUSDValue}
          />

          <LightCard>
            <AutoColumn gap="md">
              <RowBetween>
                <Text fontSize={16} fontWeight={500}>
                  <>
                    Pooled{' '}
                    {selectedUnderlyingBalances?.amount0?.currency?.symbol || fallbackCurrencies.currency0?.symbol}:
                  </>
                </Text>
                <RowFixed>
                  <Text fontSize={16} fontWeight={500} marginLeft="6px">
                    {selectedUnderlyingBalances?.amount0 ? (
                      <FormattedCurrencyAmount
                        significantDigits={6}
                        currencyAmount={selectedUnderlyingBalances.amount0}
                      />
                    ) : (
                      '0'
                    )}
                  </Text>
                  <CurrencyLogo
                    size="20px"
                    style={{ marginLeft: '8px' }}
                    currency={selectedUnderlyingBalances?.amount0?.currency || fallbackCurrencies.currency0}
                  />
                </RowFixed>
              </RowBetween>
              <RowBetween>
                <Text fontSize={16} fontWeight={500}>
                  <>
                    Pooled{' '}
                    {selectedUnderlyingBalances?.amount1?.currency?.symbol || fallbackCurrencies.currency1?.symbol}:
                  </>
                </Text>
                <RowFixed>
                  <Text fontSize={16} fontWeight={500} marginLeft="6px">
                    {selectedUnderlyingBalances?.amount1 ? (
                      <FormattedCurrencyAmount
                        significantDigits={6}
                        currencyAmount={selectedUnderlyingBalances.amount1}
                      />
                    ) : (
                      '0'
                    )}
                  </Text>
                  <CurrencyLogo
                    size="20px"
                    style={{ marginLeft: '8px' }}
                    currency={selectedUnderlyingBalances?.amount1?.currency || fallbackCurrencies.currency1}
                  />
                </RowFixed>
              </RowBetween>
            </AutoColumn>
          </LightCard>

          {showCollectAsWeth && (
            <RowBetween>
              <ThemedText.DeprecatedMain>
                <>Collect as {nativeWrappedSymbol}</>
              </ThemedText.DeprecatedMain>
              <Toggle
                id="island-withdraw-collect-as-wrapped"
                isActive={receiveWETH}
                useAlternateBackground
                toggle={() => setReceiveWETH((current) => !current)}
              />
            </RowBetween>
          )}

          {!account ? (
            <ButtonLight onClick={toggleWalletModal}>
              <>Connect Wallet</>
            </ButtonLight>
          ) : (
            <ButtonError
              onClick={() => setShowConfirm(true)}
              disabled={
                !selectedShares ||
                selectedShares.equalTo('0') ||
                !totalUserBalanceShares ||
                totalUserBalanceShares.equalTo('0')
              }
              error={!selectedShares || !totalUserBalanceShares || totalUserBalanceShares.equalTo('0')}
            >
              <Text fontSize={20} fontWeight={500}>
                {!selectedShares || selectedShares.equalTo('0') ? (
                  <>Enter an amount</>
                ) : !totalUserBalanceShares || totalUserBalanceShares.equalTo('0') ? (
                  <>No balance to withdraw</>
                ) : (
                  <>Remove</>
                )}
              </Text>
            </ButtonError>
          )}
        </AutoColumn>
      ) : (
        <Loader />
      )}
    </>
  )
}

export default WithdrawView
